import React, { createElement, forwardRef, useState } from "react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  DataGridPro,
  DataGridProProps,
  GridApiPro,
  GridColDef,
  GridColumnHeaderParams,
  GridColumnMenuProps,
  GridEventListener,
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  GridSlotsComponentsProps,
  useGridApiRef,
} from "@mui/x-data-grid-pro";

import { TestProps } from "shared/types";

import Card from "features/components/Card";
import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import ExpandIcon from "features/ui/Icons/ExpandIcon";

import CustomColumnMenu from "./components/CustomColumnMenu";
import FilterBuilderFilterPanel from "./components/FilterBuilderFilterPanel";
import {
  DEFAULT_GET_ROW_ID,
  NO_TOOLBAR,
  PINNED_RIGHT_SHIFT_VAR,
} from "./constants";
import { useColumnPersistence } from "./hooks/useColumnPersistence";
import { useHorizontalOverflowScroll } from "./hooks/useHorizontalOverflowScroll";
import type { RowData, SchemaEntry } from "./types";
import { convertSchemaDataTypeToMaterialDataType, renderCell } from "./utils";

type GridProSubset = Pick<
  DataGridProProps,
  "loading" | "rowCount" | "getRowId" | "disableColumnMenu"
>;

interface Props extends GridProSubset, TestProps {
  schema: SchemaEntry[];
  data?: RowData[];
  formatCell: (params: GridRenderCellParams) => any;
  renderHeader: (params: GridColumnHeaderParams) => any;
  // TODO mbencina: when on full functionality this will be required again
  renderPanel?: (params: GridRowParams) => React.ReactNode;
  toolbar?: React.JSXElementConstructor<any>;
  onRowHover?: (rowId: string | null) => void;
  pageKey: string; // Used for localStorage key for column order and width persistence
  columnMenu?: React.JSXElementConstructor<GridColumnMenuProps>;
  columnMenuProps?: Partial<GridColumnMenuProps> & Record<string, any>;
  // SmartMaterialTable always hides its own footer, so only `stickyHeader`
  // has meaning here. It gates the sticky infrastructure (overflow/scroll
  // overrides) so the pinned column header and any nested MaterialTable's
  // sticky can propagate up to the page-level scroll ancestor.
  stickyHeader?: boolean;
  // Filter panel wiring — same shape as MaterialTable. When `filtersSchema`
  // is provided, the grid hosts FilterBuilderFilterPanel so `apiRef.current?.
  // showFilterPanel()` opens a working editor. The schema is independent of
  // the displayed columns (smart-groups grid filters on the underlying event
  // schema, not its own group columns).
  apiRef?: React.RefObject<GridApiPro | null>;
  filtersSchema?: SchemaEntry[];
  filters?: FilterGroupState;
  updateFilters?: (next: FilterGroupState) => void;
  onPreferencePanelOpen?: () => void;
  onPreferencePanelClose?: () => void;
  pinnedRightColumns?: string[];
}

const SmartMaterialTable = ({
  schema,
  data = [],
  getRowId = DEFAULT_GET_ROW_ID,
  formatCell,
  renderHeader,
  loading,
  renderPanel,
  toolbar,
  rowCount,
  disableColumnMenu = false,
  onRowHover,
  pageKey,
  columnMenu,
  columnMenuProps,
  testId,
  stickyHeader = false,
  apiRef,
  filtersSchema,
  filters,
  updateFilters,
  onPreferencePanelOpen,
  onPreferencePanelClose,
  pinnedRightColumns,
}: Props) => {
  const internalApiRef = useGridApiRef();
  const effectiveApiRef = apiRef ?? internalApiRef;
  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(
    new Set()
  );

  const baseColumns: GridColDef[] = (schema || []).map((schemaEntry) => ({
    field: schemaEntry.accessor,
    headerName: schemaEntry.label,
    width: schemaEntry.columnWidth || 180,
    minWidth: schemaEntry.minWidth,
    flex: schemaEntry.flex,
    description:
      typeof schemaEntry.description === "string"
        ? schemaEntry.description
        : undefined,
    type: convertSchemaDataTypeToMaterialDataType(schemaEntry.dataType),
    filterable: false,
    sortable: false,
    renderCell: renderCell(schemaEntry, formatCell),
    renderHeader,
    renderPanel,
  }));

  const { columns, handleColumnOrderChange, handleColumnWidthChange } =
    useColumnPersistence(baseColumns, { pageKey, apiRef: effectiveApiRef });

  // `stickyHeader` clips the grid's own horizontal overflow, so wide columns
  // would otherwise be unreachable — this drives the scroll by hand instead.
  const {
    containerRef: hScrollContainerRef,
    scrollbarRef: hScrollbarRef,
    scrollbarProps: hScrollbarProps,
    showScrollbar: showHScrollbar,
    totalWidth: hScrollTotalWidth,
    isHeaderFullyVisible: hScrollHeaderVisible,
  } = useHorizontalOverflowScroll(stickyHeader, effectiveApiRef);

  const filterPanelEnabled = Boolean(filtersSchema);

  const panelEnabled = Boolean(renderPanel);

  const handleRowClick: GridEventListener<"rowClick"> = (params) => {
    if (effectiveApiRef.current) {
      effectiveApiRef.current.toggleDetailPanel(params.id);
    }
  };

  const slotProps: GridSlotsComponentsProps = {
    loadingOverlay: {
      variant: "linear-progress",
      noRowsVariant: "linear-progress",
    },
    // The grid drops props it does not know, so the test id has to reach its root through the slot.
    ...(testId && { root: { "data-testid": testId } }),
    row: {
      "data-testid": "smart-groups-row",
      onMouseEnter: (event: React.MouseEvent<HTMLDivElement>) => {
        if (onRowHover) {
          const rowId = event.currentTarget.getAttribute("data-id");
          if (rowId) {
            onRowHover(rowId);
          }
        }
      },
      onMouseLeave: () => {
        if (onRowHover) {
          onRowHover(null);
        }
      },
    },
    ...(columnMenuProps && { columnMenu: columnMenuProps }),
    ...(filterPanelEnabled && {
      filterPanel: {
        filterGroupState: filters,
        filtersSchema,
        onApplyFilters: updateFilters,
      },
    }),
  };

  const stickyHeaderSx = stickyHeader
    ? {
        overflowX: "clip",
        overflowY: "visible",
        "& .MuiDataGrid-mainContent": {
          overflowX: "clip",
          overflowY: "visible",
        },
        "& .MuiDataGrid-main": { overflowX: "clip", overflowY: "visible" },
        "& .MuiDataGrid-virtualScroller": {
          overflowX: "clip !important",
          overflowY: "visible !important",
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "background.paper",
        },
        "& .MuiDataGrid-filler": {
          backgroundColor: "background.paper",
        },
        "& .MuiDataGrid-scrollbar": { display: "none !important" },
        // MUI's detail panel defaults to `overflow: auto`, which creates its
        // own scroll context. That introduces a second scrollbar on the page
        // and traps `position: sticky` inside the panel — so the nested
        // MaterialTable's sticky column header can't pin to the page. Clear
        // the scroll context so the nested sticky propagates to the
        // page-level ancestor and the page keeps a single scrollbar.
        "& .MuiDataGrid-detailPanel": {
          overflowX: "clip",
          overflowY: "visible",
        },
      }
    : undefined;

  // Once the grid overflows sideways, MUI draws its scroll shadow down the
  // pinned column's left edge — but only across the body, since the header
  // cells paint over it. The header's own left border then reads as a
  // mismatched hard line, so it goes while the shadow is there.
  const pinnedRightSx = pinnedRightColumns?.length
    ? {
        "& .MuiDataGrid-virtualScroller--hasScrollX .MuiDataGrid-columnHeader--pinnedRight":
          {
            borderLeft: "none !important",
          },
        // The grid's own scroll shadow is one absolutely-positioned element
        // covering the whole scroller, so it also paints across an expanded
        // detail panel. Cast the same shadow off the pinned cells instead —
        // they exist per row and in the header but not in a detail panel, so
        // the panel stays clean. The grid's shadow fades with its own
        // scrollLeft, which never moves here because the horizontal scroll is
        // driven by transforms, so a fixed shadow matches what it drew.
        "& .MuiDataGrid-scrollShadow--horizontal": { display: "none" },
        "& .MuiDataGrid-virtualScroller--hasScrollX :is(.MuiDataGrid-cell--pinnedRight, .MuiDataGrid-columnHeader--pinnedRight)":
          {
            // A box-shadow would round off at each cell's top and bottom edge,
            // breaking the line into per-row blobs. A gradient strip keeps the
            // same weight down the full height.
            overflow: "visible",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              right: "100%",
              width: "6px",
              background:
                "linear-gradient(to left, rgba(0, 0, 0, 0.07), rgba(0, 0, 0, 0))",
              pointerEvents: "none",
            },
          },
        // `position: sticky` can't pin a column under `stickyHeader` — the
        // overflow overrides leave the grid without a scrollport, so the
        // browser resolves the pinned cells against the page instead and they
        // hang off the grid's right edge. useHorizontalOverflowScroll parks
        // them by hand.
        ...(stickyHeader && {
          "& .MuiDataGrid-columnHeader--pinnedRight, & .MuiDataGrid-cell--pinnedRight":
            {
              position: "relative",
              transform: `translateX(var(${PINNED_RIGHT_SHIFT_VAR}, 0px))`,
            },
        }),
      }
    : undefined;

  const dataGrid = (
    <DataGridPro
      sx={{ ...stickyHeaderSx, ...pinnedRightSx }}
      apiRef={effectiveApiRef}
      rows={data}
      columns={columns}
      rowHeight={60}
      autoHeight
      className={`smart-table${panelEnabled ? " with-detail-panel" : ""}`}
      getRowId={getRowId}
      paginationMode="server"
      sortingMode="server"
      filterMode="server"
      loading={loading}
      rowCount={rowCount ?? 0}
      slots={{
        columnMenu: columnMenu || CustomColumnMenu,
        ...(filterPanelEnabled && { filterPanel: FilterBuilderFilterPanel }),
        ...(panelEnabled && {
          detailPanelExpandIcon: () => <ExpandIcon />,
          detailPanelCollapseIcon: () => <ExpandLessIcon />,
        }),
        toolbar: toolbar
          ? forwardRef((props, ref) =>
              createElement(toolbar, { ...props, ref })
            )
          : NO_TOOLBAR,
      }}
      showToolbar={!!toolbar}
      slotProps={slotProps}
      initialState={{ pinnedColumns: { right: pinnedRightColumns } }}
      getDetailPanelHeight={() => "auto"}
      getDetailPanelContent={renderPanel}
      onRowClick={panelEnabled ? handleRowClick : undefined}
      onPreferencePanelOpen={onPreferencePanelOpen}
      onPreferencePanelClose={onPreferencePanelClose}
      onColumnOrderChange={handleColumnOrderChange}
      onColumnWidthChange={handleColumnWidthChange}
      hideFooter
      disableColumnMenu={disableColumnMenu}
      disableRowSelectionOnClick
      detailPanelExpandedRowIds={expandedRowIds}
      onDetailPanelExpandedRowIdsChange={(ids) => setExpandedRowIds(ids)}
      getRowClassName={({ id }) =>
        expandedRowIds.has(id) ? "data-row-expanded" : "data-row-collapsed"
      }
    />
  );

  return (
    <Card className={`w-full !p-0${stickyHeader ? " !overflow-clip" : ""}`}>
      {stickyHeader ? (
        <div ref={hScrollContainerRef}>{dataGrid}</div>
      ) : (
        dataGrid
      )}
      {showHScrollbar && hScrollHeaderVisible && (
        <div
          ref={hScrollbarRef}
          {...hScrollbarProps}
          className="sticky bottom-0 z-3 overflow-x-auto overflow-y-hidden"
          data-testid="smart-material-table-horizontal-scrollbar"
        >
          <div style={{ width: hScrollTotalWidth }} className="h-[10px]" />
        </div>
      )}
    </Card>
  );
};

export default SmartMaterialTable;
